import { useEffect, useState } from "react";
import { BadgeCheck, Eye, MapPin } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { BadgeIcon, SocialIcon, SOCIAL } from "./Icons";
import { MusicPlayer } from "./MusicPlayer";
import { asset } from "../lib/api";

const useTypewriter = (text, on) => {
  const [out, setOut] = useState(on ? "" : text);
  useEffect(() => {
    if (!on) { setOut(text); return; }
    let i = 0;
    setOut("");
    const id = setInterval(() => { i += 1; setOut(text.slice(0, i)); if (i >= text.length) clearInterval(id); }, 45);
    return () => clearInterval(id);
  }, [text, on]);
  return out;
};

const shapeCls = { circle: "rounded-full", rounded: "rounded-2xl", square: "rounded-md" };

const Avatar = ({ p, size = "h-24 w-24" }) => (
  <div className={`relative ${size} shrink-0`}>
    {p.glow && <div className={`absolute -inset-1 ${shapeCls[p.avatar_shape]} opacity-70 blur-md animate-pulse-glow`} style={{ background: p.accent_color }} />}
    {p.avatar_url ? (
      <img data-testid="profile-avatar" src={asset(p.avatar_url)} alt="" className={`relative ${size} ${shapeCls[p.avatar_shape]} border-2 border-black/40 object-cover`} />
    ) : (
      <div data-testid="profile-avatar" className={`relative flex ${size} ${shapeCls[p.avatar_shape]} items-center justify-center bg-[#181820] font-display text-3xl font-bold text-white`}>
        {(p.display_name || p.username || "?").slice(0, 1).toUpperCase()}
      </div>
    )}
  </div>
);

const Name = ({ p, className = "text-3xl" }) => (
  <h1 data-testid="profile-display-name" className={`flex items-center gap-2 font-display font-bold tracking-tight text-white ${className}`} style={p.glow ? { textShadow: `0 0 18px ${p.accent_color}88` } : undefined}>
    <span className="truncate">{p.display_name || p.username}</span>
    {p.verified && <BadgeCheck data-testid="profile-verified-badge" className="h-6 w-6 shrink-0 fill-[#1d9bf0] text-white" />}
  </h1>
);

const Badges = ({ p }) => !p.badges?.length ? null : (
  <TooltipProvider delayDuration={100}>
    <div data-testid="profile-badges" className="flex flex-wrap items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-2.5 py-1.5">
      {p.badges.map((b) => (
        <Tooltip key={b.id}>
          <TooltipTrigger asChild>
            <span data-testid={`profile-badge-${b.id}`} className="cursor-default transition-transform hover:scale-125"><BadgeIcon icon={b.icon} className="h-4 w-4" style={{ color: b.color, filter: `drop-shadow(0 0 6px ${b.color})` }} /></span>
          </TooltipTrigger>
          <TooltipContent className="border-[#262630] bg-[#0d0d10] text-xs text-white">{b.name}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  </TooltipProvider>
);

const linkProps = (l) => ({ href: l.platform === "discord" && !l.url.startsWith("http") ? undefined : l.url, target: "_blank", rel: "noopener noreferrer", title: l.label || SOCIAL[l.platform]?.label });

const IconLinks = ({ p }) => (
  <div data-testid="profile-links" className="flex flex-wrap justify-center gap-3">
    {p.links.map((l) => (
      <a key={l.id} data-testid={`profile-link-${l.platform}`} {...linkProps(l)} className="text-zinc-200 transition-all duration-200 hover:-translate-y-0.5 hover:text-white" style={{ filter: p.glow ? `drop-shadow(0 0 6px ${p.accent_color}aa)` : undefined }}>
        <SocialIcon platform={l.platform} className="h-6 w-6" />
      </a>
    ))}
  </div>
);

const ButtonLinks = ({ p }) => (
  <div data-testid="profile-links" className="flex w-full flex-col gap-2">
    {p.links.map((l) => (
      <a key={l.id} data-testid={`profile-link-${l.platform}`} {...linkProps(l)} className="group flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm font-medium text-white transition-all duration-200 hover:translate-x-1" onMouseEnter={(e) => (e.currentTarget.style.borderColor = p.accent_color)} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}>
        <SocialIcon platform={l.platform} className="h-5 w-5" />
        <span className="truncate">{l.label || (l.platform === "discord" && !l.url.startsWith("http") ? l.url : SOCIAL[l.platform]?.label)}</span>
      </a>
    ))}
  </div>
);

const Meta = ({ p }) => (
  <div className="flex items-center gap-4 font-mono text-[11px] text-zinc-400">
    {p.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>}
    {p.show_views && <span data-testid="profile-view-count" className="flex items-center gap-1"><Eye className="h-3 w-3" />{(p.views || 0).toLocaleString()}</span>}
  </div>
);

const Bio = ({ p, className = "" }) => {
  const text = useTypewriter(p.bio || "", p.typewriter_bio);
  if (!p.bio) return null;
  return <p data-testid="profile-bio" className={`whitespace-pre-line text-sm leading-relaxed text-zinc-300 ${p.typewriter_bio ? "caret" : ""} ${className}`} style={{ "--accent": p.accent_color }}>{text}</p>;
};

const cardStyle = (p) => ({ background: `rgba(10,10,12,${p.card_opacity / 100})`, borderColor: p.glow ? `${p.accent_color}40` : "rgba(255,255,255,.08)", boxShadow: p.glow ? `0 0 60px ${p.accent_color}22` : undefined });

const Classic = ({ p, play }) => (
  <div className="w-full max-w-md rounded-3xl border p-8 backdrop-blur-xl" style={cardStyle(p)}>
    <div className="flex flex-col items-center gap-4 text-center">
      <Avatar p={p} />
      <Name p={p} />
      <Badges p={p} />
      <Bio p={p} />
      {p.links.length > 0 && <IconLinks p={p} />}
      {p.music_url && <MusicPlayer src={p.music_url} title={p.music_title} accent={p.accent_color} autoPlay={play} />}
      <Meta p={p} />
    </div>
  </div>
);

const CardLayout = ({ p, play }) => (
  <div className="w-full max-w-xl rounded-3xl border p-7 backdrop-blur-xl" style={cardStyle(p)}>
    <div className="flex items-center gap-5">
      <Avatar p={p} size="h-20 w-20" />
      <div className="min-w-0 space-y-2">
        <Name p={p} className="text-2xl" />
        <Badges p={p} />
      </div>
    </div>
    <Bio p={p} className="mt-5" />
    {p.links.length > 0 && <div className="mt-5"><ButtonLinks p={p} /></div>}
    {p.music_url && <div className="mt-4"><MusicPlayer src={p.music_url} title={p.music_title} accent={p.accent_color} autoPlay={play} /></div>}
    <div className="mt-5"><Meta p={p} /></div>
  </div>
);

const Minimal = ({ p, play }) => (
  <div className="flex w-full max-w-md flex-col items-start gap-4">
    <Avatar p={p} size="h-16 w-16" />
    <Name p={p} className="text-4xl" />
    <Badges p={p} />
    <Bio p={p} className="text-base" />
    {p.links.length > 0 && <div className="flex flex-col gap-1.5">{p.links.map((l) => (
      <a key={l.id} data-testid={`profile-link-${l.platform}`} {...linkProps(l)} className="group flex items-center gap-2 font-mono text-sm text-zinc-300 transition-colors hover:text-white">
        <span style={{ color: p.accent_color }}>→</span>{l.label || SOCIAL[l.platform]?.label}
      </a>))}</div>}
    {p.music_url && <MusicPlayer src={p.music_url} title={p.music_title} accent={p.accent_color} autoPlay={play} />}
    <Meta p={p} />
  </div>
);

const Split = ({ p, play }) => (
  <div className="grid w-full max-w-3xl gap-6 rounded-3xl border p-7 backdrop-blur-xl md:grid-cols-2" style={cardStyle(p)}>
    <div className="flex flex-col items-start gap-4">
      <Avatar p={p} size="h-28 w-28" />
      <Name p={p} />
      <Badges p={p} />
      <Bio p={p} />
      <Meta p={p} />
    </div>
    <div className="flex flex-col justify-center gap-3">
      {p.links.length > 0 && <ButtonLinks p={p} />}
      {p.music_url && <MusicPlayer src={p.music_url} title={p.music_title} accent={p.accent_color} autoPlay={play} />}
    </div>
  </div>
);

const LAYOUTS = { classic: Classic, card: CardLayout, minimal: Minimal, split: Split };

export const ProfileBackground = ({ p, fixed = true }) => (
  <div className={`${fixed ? "fixed" : "absolute"} inset-0 overflow-hidden`}>
    {p.background_url ? (
      <img src={asset(p.background_url)} alt="" className="h-full w-full scale-105 object-cover" style={{ filter: `blur(${p.bg_blur}px)` }} />
    ) : (
      <div className="h-full w-full bg-[#080808]">
        <div className="absolute -left-20 top-1/4 h-96 w-96 rounded-full opacity-30 blur-[120px] animate-drift" style={{ background: p.accent_color }} />
        <div className="absolute -right-10 bottom-10 h-80 w-80 rounded-full bg-[#800d43] opacity-30 blur-[120px] animate-drift" style={{ animationDelay: "-6s" }} />
      </div>
    )}
    <div className="absolute inset-0 bg-black" style={{ opacity: p.bg_dim / 100 }} />
  </div>
);

export const ProfileView = ({ profile, play = false }) => {
  const L = LAYOUTS[profile.layout] || Classic;
  return <div data-testid={`profile-layout-${profile.layout}`} className="relative z-10 flex w-full justify-center animate-fade-up"><L p={{ ...profile, links: profile.links || [] }} play={play} /></div>;
};
